import {
	AtlBrowserStorageLocal,
	type AtlBrowserStorage,
} from '@atlassian/browser-storage-controls/web-storage-service';

import { ErrorWithCause, normalizeErrorCause, type ErrorCause } from '../errors';
import type { JsonPrimitive, JsonValue } from '../json';

export type LocalStorageJsonPrimitive = JsonPrimitive;

export type LocalStorageJsonValue = JsonValue;

export type LocalStorageErrorHandler = (storageError: Error) => void;

export type BrowserLocalStorage = AtlBrowserStorage;

const createStorageError = (message: string, cause?: ErrorCause): Error =>
	new ErrorWithCause(message, cause !== undefined ? { cause } : undefined);

/**
 * localStorage access can be unavailable before an operation even runs,
 * notably in sandboxed iframes and some private browsing modes. Keep that
 * browser quirk in one place so consumers can treat persistence as optional.
 */
export const getBrowserLocalStorage = (
	onError?: LocalStorageErrorHandler,
): BrowserLocalStorage | null => {
	if (typeof window === 'undefined') {
		return null;
	}

	if (!AtlBrowserStorageLocal.isAvailable) {
		onError?.(createStorageError('Failed to access browser localStorage.'));
		return null;
	}

	return AtlBrowserStorageLocal;
};

export const readLocalStorageItem = (
	storage: BrowserLocalStorage | null,
	key: string,
	onError?: LocalStorageErrorHandler,
): string | null => {
	if (storage === null) {
		return null;
	}

	try {
		return storage.getItem(key);
	} catch (error) {
		onError?.(createStorageError('Failed to read localStorage.', normalizeErrorCause(error)));
		return null;
	}
};

type WriteLocalStorageItemOptions = {
	/** Report consent-blocked writes through `onError` instead of ignoring them. */
	reportBlocked?: boolean;
};

/**
 * Writes a consent-gated key. Resolves to true only when the write landed;
 * a consent-blocked write resolves to false and is only reported when
 * `reportBlocked` is set.
 */
export const writeLocalStorageItem = async (
	storage: BrowserLocalStorage | null,
	key: string,
	value: string,
	onError?: LocalStorageErrorHandler,
	{ reportBlocked = false }: WriteLocalStorageItemOptions = {},
): Promise<boolean> => {
	if (storage === null) {
		return false;
	}

	try {
		const result = await storage.setItem(key, value);
		if (result.status === 'SUCCESS') {
			return true;
		}

		if (result.status === 'BLOCKED') {
			if (reportBlocked) {
				onError?.(createStorageError('Blocked writing localStorage by browser storage controls.'));
			}
		} else {
			onError?.(
				createStorageError(
					'Failed to write localStorage.',
					result.status === 'ERROR' ? normalizeErrorCause(result.error) : undefined,
				),
			);
		}
		return false;
	} catch (error) {
		onError?.(createStorageError('Failed to write localStorage.', normalizeErrorCause(error)));
		return false;
	}
};

/**
 * Writes a STRICTLY_NECESSARY key synchronously. The browser storage controls
 * swallow write failures for these keys, so the value is read back to confirm
 * it landed; callers use the result to detect storage that cannot be written.
 */
export const writeStrictlyNecessaryLocalStorageItem = (
	storage: BrowserLocalStorage | null,
	key: string,
	value: string,
	onError?: LocalStorageErrorHandler,
): boolean => {
	if (storage === null) {
		return false;
	}

	try {
		storage.setStrictlyNecessaryItem(key, value);
		if (storage.getItem(key) === value) {
			return true;
		}

		onError?.(createStorageError('Failed to write localStorage.'));
	} catch (error) {
		onError?.(createStorageError('Failed to write localStorage.', normalizeErrorCause(error)));
	}

	return false;
};

export const removeLocalStorageItem = (
	storage: BrowserLocalStorage | null,
	key: string,
	onError?: LocalStorageErrorHandler,
): boolean => {
	if (storage === null) {
		return false;
	}

	try {
		storage.removeItem(key);
		return true;
	} catch (error) {
		onError?.(createStorageError('Failed to remove localStorage.', normalizeErrorCause(error)));
		return false;
	}
};

/**
 * Returns null when storage is unavailable, the key is missing, JSON parsing
 * fails, validation throws, or parseValue rejects the parsed value by returning
 * null. A stored JSON "null" value is still passed to parseValue; if callers
 * need to distinguish that from a missing key, parseValue should return an
 * explicit wrapper instead of null.
 */
export const readLocalStorageJson = <TValue>(
	storage: BrowserLocalStorage | null,
	key: string,
	parseValue: (value: LocalStorageJsonValue) => TValue | null,
	onError?: LocalStorageErrorHandler,
): TValue | null => {
	const rawValue = readLocalStorageItem(storage, key, onError);
	if (rawValue === null) {
		return null;
	}

	let parsedValue: LocalStorageJsonValue;
	try {
		parsedValue = JSON.parse(rawValue) as LocalStorageJsonValue;
	} catch (error) {
		onError?.(createStorageError('Failed to parse localStorage JSON.', normalizeErrorCause(error)));
		return null;
	}

	try {
		return parseValue(parsedValue);
	} catch (error) {
		onError?.(
			createStorageError('Failed to validate localStorage JSON.', normalizeErrorCause(error)),
		);
		return null;
	}
};

export const writeStrictlyNecessaryLocalStorageJson = <TValue>(
	storage: BrowserLocalStorage | null,
	key: string,
	value: TValue,
	onError?: LocalStorageErrorHandler,
): boolean => {
	if (storage === null) {
		return false;
	}

	try {
		return writeStrictlyNecessaryLocalStorageItem(storage, key, JSON.stringify(value), onError);
	} catch (error) {
		onError?.(
			createStorageError('Failed to serialize localStorage JSON.', normalizeErrorCause(error)),
		);
		return false;
	}
};
